import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Phone, 
  PhoneCall, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Calendar,
  User,
  MapPin,
  Award,
  MessageSquare,
  TrendingUp,
  Users,
  Target,
  Star,
  Building2,
  Home,
  Bell,
  Video,
  BookOpen,
  FileText,
  Download,
  Play
} from 'lucide-react';
import { 
  demoAgencyData, 
  demoCandidates, 
  demoFeedPosts,
  demoStats,
  simulateApiCall 
} from './demoData';
import DemoMessaggi from './DemoMessaggi';
import DemoNotifiche from './DemoNotifiche';
import DemoFeedVideo from './DemoFeedVideo';

const DemoAgenzia = () => {
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [stats, setStats] = useState(demoStats.agenzia);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('tutti');
  const [showCallModal, setShowCallModal] = useState(false);
  const [showEsitoModal, setShowEsitoModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [callNotes, setCallNotes] = useState('');
  const [feedPosts] = useState(demoFeedPosts);
  const [activeTab, setActiveTab] = useState('home');

  // Navigation items
  const navItems = [
    { key: 'home', icon: Home, label: 'Home' },
    { key: 'match', icon: Users, label: 'Match' },
    { key: 'formazione', icon: BookOpen, label: 'Formazione' },
    { key: 'messaggi', icon: MessageSquare, label: 'Messaggi' },
    { key: 'notifiche', icon: Bell, label: 'Notifiche' },
    { key: 'feed', icon: Video, label: 'Feed' }
  ];

  // Simulazione caricamento dati
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const data = await simulateApiCall(demoCandidates, 800);
      setCandidates(data);
      setLoading(false);
    };
    loadData();
  }, []);

  // Filtri candidati
  useEffect(() => {
    filterCandidates();
  }, [candidates, activeFilter]);

  const filterCandidates = () => {
    let filtered = candidates;
    
    switch (activeFilter) {
      case 'da_chiamare':
        filtered = candidates.filter(c => c.stato_match === 'in_attesa_contatto');
        break;
      case 'colloquio_fissato':
        filtered = candidates.filter(c => c.stato_match === 'colloquio_fissato');
        break;
      case 'inseriti':
        filtered = candidates.filter(c => c.stato_match === 'inserito');
        break;
      default:
        filtered = candidates;
    }
    
    setFilteredCandidates(filtered);
  };

  const handleCallClick = (candidate) => {
    setSelectedCandidate(candidate);
    setShowCallModal(true);
  };

  const handleSegnaChiamato = (candidate) => {
    setSelectedCandidate(candidate);
    setShowEsitoModal(true);
  };

  const handleEsitoSubmit = (esito) => {
    // Simulazione aggiornamento stato (solo frontend)
    const updatedCandidates = candidates.map(c => {
      if (c.id === selectedCandidate.id) {
        let newStatus = 'chiamato';
        let updatedCandidate = { ...c };
        
        if (esito === 'interessato') {
          newStatus = 'colloquio_fissato';
          updatedCandidate.data_colloquio = '2024-10-15';
        } else if (esito === 'non_interessato') {
          newStatus = 'non_interessato';
        }
        
        updatedCandidate.stato_match = newStatus;
        if (callNotes) {
          updatedCandidate.note_agenzia = callNotes;
        }
        
        return updatedCandidate;
      }
      return c;
    });

    setCandidates(updatedCandidates);
    
    // Aggiorna stats simulati
    const newStats = { ...stats };
    if (esito === 'interessato') {
      newStats.colloqui_fissati += 1;
    }
    setStats(newStats);
    
    setShowEsitoModal(false);
    setCallNotes('');
    
    // Toast simulato
    console.log(`✅ Demo: Candidato ${selectedCandidate.nome} aggiornato a ${esito}`);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'in_attesa_contatto': { color: 'bg-yellow-100 text-yellow-800', text: 'Da chiamare' },
      'chiamato': { color: 'bg-blue-100 text-blue-800', text: 'Chiamato' },
      'colloquio_fissato': { color: 'bg-green-100 text-green-800', text: 'Colloquio fissato' },
      'inserito': { color: 'bg-purple-100 text-purple-800', text: 'Inserito' },
      'non_interessato': { color: 'bg-red-100 text-red-800', text: 'Non interessato' }
    };
    
    const statusInfo = statusMap[status] || { color: 'bg-gray-100 text-gray-800', text: status };
    return <Badge className={statusInfo.color}>{statusInfo.text}</Badge>;
  };

  const filters = [
    { key: 'tutti', label: 'Tutti', icon: TrendingUp },
    { key: 'da_chiamare', label: 'Da chiamare', icon: Phone },
    { key: 'colloquio_fissato', label: 'Colloquio fissato', icon: Calendar },
    { key: 'inseriti', label: 'Inseriti', icon: CheckCircle }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento dashboard agenzia...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50">
      {/* Header Agenzia */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{demoAgencyData.nome_agenzia}</h1>
                <p className="text-gray-600">Dashboard Agenzia • {demoAgencyData.zona}</p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex gap-2">
              {navItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <Button
                    key={item.key}
                    variant={activeTab === item.key ? 'default' : 'outline'}
                    onClick={() => setActiveTab(item.key)}
                    className="rounded-xl"
                  >
                    <IconComponent className="w-4 h-4 mr-2" />
                    {item.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 md:pb-8">
        {activeTab === 'home' && (
          <div className="space-y-6">
            {/* Dashboard Home Content */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{stats.match_attivi}</div>
                  <div className="text-sm opacity-90">Match Attivi</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white border-0">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{stats.colloqui_questa_settimana}</div>
                  <div className="text-sm opacity-90">Colloqui Settimana</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{stats.inseriti_questo_mese}</div>
                  <div className="text-sm opacity-90">Inseriti Mese</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{stats.tasso_conversione}%</div>
                  <div className="text-sm opacity-90">Tasso Conversione</div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4">Azioni Rapide</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Button
                    onClick={() => setActiveTab('match')}
                    className="h-20 flex flex-col items-center gap-2 bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                    variant="outline"
                  >
                    <Users className="w-6 h-6" />
                    <span>Gestisci Match</span>
                  </Button>
                  <Button
                    onClick={() => setActiveTab('messaggi')}
                    className="h-20 flex flex-col items-center gap-2 bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                    variant="outline"
                  >
                    <MessageSquare className="w-6 h-6" />
                    <span>Messaggi</span>
                  </Button>
                  <Button
                    onClick={() => setActiveTab('feed')}
                    className="h-20 flex flex-col items-center gap-2 bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                    variant="outline"
                  >
                    <Video className="w-6 h-6" />
                    <span>Crea Post</span>
                  </Button>
                  <Button
                    onClick={() => setActiveTab('notifiche')}
                    className="h-20 flex flex-col items-center gap-2 bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100"
                    variant="outline"
                  >
                    <Bell className="w-6 h-6" />
                    <span>Notifiche</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'match' && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{stats.match_attivi}</div>
                  <div className="text-sm opacity-90">Match Attivi</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white border-0">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{stats.colloqui_questa_settimana}</div>
                  <div className="text-sm opacity-90">Colloqui Settimana</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{stats.inseriti_questo_mese}</div>
                  <div className="text-sm opacity-90">Inseriti Mese</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{stats.tasso_conversione}%</div>
                  <div className="text-sm opacity-90">Tasso Conversione</div>
                </CardContent>
              </Card>
            </div>

            {/* Filtri */}
            <div className="flex flex-wrap gap-2">
              {filters.map((filter) => {
                const IconComponent = filter.icon;
                return (
                  <Button
                    key={filter.key}
                    variant={activeFilter === filter.key ? "default" : "outline"}
                    onClick={() => setActiveFilter(filter.key)}
                    className={`flex items-center gap-2 rounded-xl ${
                      activeFilter === filter.key
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                        : 'hover:bg-blue-50'
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    {filter.label}
                  </Button>
                );
              })}
            </div>

            {/* Lista Candidati */}
            <div className="grid gap-4">
              {filteredCandidates.map((candidate) => (
                <Card key={candidate.id} className="hover:shadow-lg transition-shadow duration-200">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row gap-4">
                      {/* Avatar e Info Candidato */}
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {candidate.foto}
                        </div>
                        
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg">{candidate.nome} {candidate.cognome}</h3>
                            {getStatusBadge(candidate.stato_match)}
                          </div>
                          
                          {/* Dettagli Completi Candidato */}
                          <div className="bg-gray-50 p-4 rounded-xl mb-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <User className="w-4 h-4 text-blue-600" />
                                  <span className="font-semibold">Età:</span>
                                  <span>{candidate.eta} anni</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-green-600" />
                                  <span className="font-semibold">Zona:</span>
                                  <span>{candidate.zona_interesse}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Award className="w-4 h-4 text-purple-600" />
                                  <span className="font-semibold">Esperienza:</span>
                                  <span>{candidate.anni_esperienza} {candidate.anni_esperienza === 1 ? 'anno' : 'anni'}</span>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <Phone className="w-4 h-4 text-orange-600" />
                                  <span className="font-semibold">Telefono:</span>
                                  <span>{candidate.telefono}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <MessageSquare className="w-4 h-4 text-indigo-600" />
                                  <span className="font-semibold">Email:</span>
                                  <span className="text-xs">{candidate.email}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Target className="w-4 h-4 text-red-600" />
                                  <span className="font-semibold">Compatibilità:</span>
                                  <Badge className="bg-green-100 text-green-800 text-xs px-2">
                                    {candidate.score}% Match
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            
                            {/* Indicatori Esperienza */}
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                  {candidate.esperienza_flag ? (
                                    <>
                                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                      <span className="text-xs text-green-700 font-semibold">Con esperienza settore</span>
                                    </>
                                  ) : (
                                    <>
                                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                                      <span className="text-xs text-orange-700 font-semibold">Neolaureato/Prima esperienza</span>
                                    </>
                                  )}
                                </div>
                                
                                {candidate.anni_esperienza >= 3 && (
                                  <Badge className="bg-blue-100 text-blue-800 text-xs">
                                    Profilo Senior
                                  </Badge>
                                )}
                                
                                {candidate.score >= 90 && (
                                  <Badge className="bg-purple-100 text-purple-800 text-xs">
                                    Top Match
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>

                          {candidate.note_agenzia && (
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <div className="flex items-start gap-2">
                                <MessageSquare className="w-4 h-4 text-blue-600 mt-0.5" />
                                <div className="text-sm text-blue-700">
                                  <strong>Nota interna:</strong> {candidate.note_agenzia}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Azioni */}
                      <div className="flex flex-col gap-2 min-w-[200px]">
                        {candidate.stato_match === 'in_attesa_contatto' && (
                          <>
                            <Button
                              onClick={() => handleCallClick(candidate)}
                              className="bg-green-500 hover:bg-green-600 text-white rounded-xl flex items-center gap-2"
                            >
                              <Phone className="w-4 h-4" />
                              Chiama ora
                            </Button>
                            <Button
                              onClick={() => handleSegnaChiamato(candidate)}
                              variant="outline"
                              className="rounded-xl flex items-center gap-2"
                            >
                              <PhoneCall className="w-4 h-4" />
                              Segna come chiamato
                            </Button>
                          </>
                        )}

                        {candidate.stato_match === 'chiamato' && (
                          <Button
                            onClick={() => handleSegnaChiamato(candidate)}
                            variant="outline"
                            className="rounded-xl flex items-center gap-2"
                          >
                            <Calendar className="w-4 h-4" />
                            Imposta colloquio
                          </Button>
                        )}

                        {candidate.stato_match === 'colloquio_fissato' && (
                          <div className="text-sm text-center p-2 bg-green-50 rounded-xl">
                            <div className="font-semibold text-green-800">Colloquio fissato</div>
                            <div className="text-green-600">
                              {candidate.data_colloquio && new Date(candidate.data_colloquio).toLocaleDateString()}
                            </div>
                          </div>
                        )}

                        {candidate.stato_match === 'inserito' && (
                          <div className="text-sm text-center p-2 bg-purple-50 rounded-xl">
                            <CheckCircle className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                            <div className="font-semibold text-purple-800">Inserito!</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'messaggi' && (
          <DemoMessaggi userType="agenzia" />
        )}

        {activeTab === 'notifiche' && (
          <DemoNotifiche userType="agenzia" />
        )}

        {activeTab === 'formazione' && (
          <div className="space-y-6">
            {/* Header Formazione */}
            <Card className="border-0 shadow-lg rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <BookOpen className="w-6 h-6 text-green-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Formazione & Team</h2>
                </div>
                <p className="text-gray-600">
                  Risorse e strumenti per migliorare la gestione del tuo team
                </p>
              </CardContent>
            </Card>

            {/* Contenuti Demo Formazione Agenzia */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Articolo 1 */}
              <Card className="border-0 shadow-lg rounded-2xl hover:shadow-xl transition-shadow overflow-hidden">
                <div className="relative h-48">
                  <img 
                    src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800" 
                    alt="Motivazione Team"
                    className="w-full h-full object-cover"
                  />
                  <Badge className="absolute top-3 right-3 bg-green-500 text-white border-0">
                    <FileText className="w-3 h-3 mr-1" />
                    Articolo
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-2">
                    Come Motivare il Tuo Team
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Strategie concrete per mantenere alto il morale e la produttività del tuo team.
                  </p>
                  <div className="flex gap-2 mb-4">
                    <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded">#motivazione</span>
                    <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded">#leadership</span>
                  </div>
                  <Button className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl">
                    Leggi Articolo
                  </Button>
                </CardContent>
              </Card>

              {/* PDF */}
              <Card className="border-0 shadow-lg rounded-2xl hover:shadow-xl transition-shadow overflow-hidden">
                <div className="relative h-48 bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                  <Download className="w-16 h-16 text-white" />
                  <Badge className="absolute top-3 right-3 bg-blue-500 text-white border-0">
                    <FileText className="w-3 h-3 mr-1" />
                    PDF
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-2">
                    Guida: Ridurre il Turnover
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Scarica la guida completa con strategie pratiche per ridurre il turnover del personale.
                  </p>
                  <div className="flex gap-2 mb-4">
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded">#turnover</span>
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded">#retention</span>
                  </div>
                  <Button className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl">
                    <Download className="w-4 h-4 mr-2" />
                    Scarica PDF
                  </Button>
                </CardContent>
              </Card>

              {/* Articolo 2 */}
              <Card className="border-0 shadow-lg rounded-2xl hover:shadow-xl transition-shadow overflow-hidden">
                <div className="relative h-48">
                  <img 
                    src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800" 
                    alt="Colloqui"
                    className="w-full h-full object-cover"
                  />
                  <Badge className="absolute top-3 right-3 bg-green-500 text-white border-0">
                    <FileText className="w-3 h-3 mr-1" />
                    Articolo
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-2">
                    Migliorare i Colloqui di Selezione
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Tecniche avanzate per condurre colloqui efficaci e identificare i migliori talenti.
                  </p>
                  <div className="flex gap-2 mb-4">
                    <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded">#colloqui</span>
                    <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded">#selezione</span>
                  </div>
                  <Button className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl">
                    Leggi Articolo
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'feed' && (
          <DemoFeedVideo userType="agenzia" />
        )}
      </div>

      {/* Modal Chiama Candidato */}
      {showCallModal && selectedCandidate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Chiama {selectedCandidate.nome}</h3>
            
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-xl">
                <div className="text-sm text-gray-600">Numero di telefono:</div>
                <div className="text-lg font-semibold">{selectedCandidate.telefono}</div>
              </div>
              
              <div>
                <label className="text-sm text-gray-600">Note per la chiamata:</label>
                <textarea
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  className="w-full mt-1 p-3 border rounded-xl resize-none"
                  rows="3"
                  placeholder="Aggiungi note sulla chiamata..."
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                onClick={() => setShowCallModal(false)}
                variant="outline"
                className="flex-1 rounded-xl"
              >
                Chiudi
              </Button>
              <Button
                onClick={() => {
                  setShowCallModal(false);
                  handleSegnaChiamato(selectedCandidate);
                }}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-xl"
              >
                Segna come chiamato
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Esito Chiamata */}
      {showEsitoModal && selectedCandidate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Esito chiamata - {selectedCandidate.nome}</h3>
            
            <div className="space-y-3">
              <Button
                onClick={() => handleEsitoSubmit('interessato')}
                className="w-full bg-green-500 hover:bg-green-600 text-white rounded-xl flex items-center justify-center gap-2 p-3"
              >
                <CheckCircle className="w-5 h-5" />
                ✅ Interessato → Imposta colloquio
              </Button>
              
              <Button
                onClick={() => handleEsitoSubmit('da_richiamare')}
                variant="outline"
                className="w-full rounded-xl flex items-center justify-center gap-2 p-3"
              >
                <Clock className="w-5 h-5" />
                ⏳ Da richiamare
              </Button>
              
              <Button
                onClick={() => handleEsitoSubmit('non_interessato')}
                variant="outline"
                className="w-full text-red-600 border-red-200 hover:bg-red-50 rounded-xl flex items-center justify-center gap-2 p-3"
              >
                <XCircle className="w-5 h-5" />
                ❌ Non interessato
              </Button>
            </div>

            <div className="mt-4">
              <label className="text-sm text-gray-600">Note interne (facoltativo):</label>
              <textarea
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                className="w-full mt-1 p-3 border rounded-xl resize-none"
                rows="2"
                placeholder="Aggiungi note interne..."
              />
            </div>

            <Button
              onClick={() => {
                setShowEsitoModal(false);
                setCallNotes('');
              }}
              variant="outline"
              className="w-full mt-4 rounded-xl"
            >
              Annulla
            </Button>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-2 z-40">
        <div className="flex justify-around items-center">
          {navItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <button
                key={item.key}
                onClick={() => setActiveTab(item.key)}
                className={`flex flex-col items-center gap-1 py-2 px-3 rounded-lg transition-colors ${
                  activeTab === item.key
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <IconComponent className="w-5 h-5" />
                <span className="text-xs font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DemoAgenzia;